import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Package, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  Loader2,
  Bot,
  Megaphone,
  Clock,
  Phone,
  DollarSign,
  Edit3,
  Save,
  X
} from 'lucide-react';
import { adminResourcesAPI } from '../../services/api';

const PLAN_COLORS = {
  free_trial: 'bg-gray-500',
  starter: 'bg-blue-500',
  professional: 'bg-purple-500',
  enterprise: 'bg-amber-500'
};

const ClientResourceManager = () => {
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientQuota, setClientQuota] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [editMode, setEditMode] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overviewRes, plansRes] = await Promise.all([
        adminResourcesAPI.getOverview(),
        adminResourcesAPI.getQuotaPlans()
      ]);
      setClients(overviewRes.data.clients || []);
      setPlans(plansRes.data.plans || {});
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyPlan = async (clientId, planName) => {
    try {
      setUpdating(clientId);
      await adminResourcesAPI.applyPlan(clientId, planName);
      await fetchData();
      if (expandedClient === clientId) {
        await fetchClientQuota(clientId);
      }
    } catch (err) {
      console.error('Error applying plan:', err);
    } finally {
      setUpdating(null);
    }
  };

  const fetchClientQuota = async (clientId) => {
    try {
      const res = await adminResourcesAPI.getClientQuota(clientId);
      setClientQuota(res.data);
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  };

  const toggleExpand = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
      setClientQuota(null);
      setEditMode(null);
    } else {
      setExpandedClient(clientId);
      await fetchClientQuota(clientId);
    }
  };

  const startEdit = (clientId) => {
    if (clientQuota?.quota) {
      setEditValues({
        max_voice_agents: clientQuota.quota.max_voice_agents || 5,
        max_campaigns: clientQuota.quota.max_campaigns || 10,
        max_end_users: clientQuota.quota.max_end_users || 20,
        max_minutes_per_month: clientQuota.quota.max_minutes_per_month || 5000,
        max_concurrent_calls: clientQuota.quota.max_concurrent_calls || 2,
        price_per_minute_usd: clientQuota.quota.price_per_minute_usd || 0.05
      });
      setEditMode(clientId);
    }
  };

  const cancelEdit = () => {
    setEditMode(null);
    setEditValues({});
  };

  const saveCustomQuota = async (clientId) => {
    try {
      setUpdating(clientId);
      await adminResourcesAPI.updateClientQuota(clientId, editValues);
      await fetchClientQuota(clientId);
      await fetchData();
      setEditMode(null);
    } catch (err) {
      console.error('Error updating quota:', err);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resource-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Resource Quota Management</h2>
          <p className="text-gray-400">Manage client quotas based on packages and payments</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Users className="w-4 h-4" />
          <span>{clients.length} Total Clients</span>
        </div>
      </div>

      {/* Plans Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(plans).map(([planName, planData]) => (
          <div key={planName} className="p-4 rounded-xl border border-gray-700 bg-gray-800/50 hover:border-gray-600 transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className={`px-2 py-1 rounded text-xs font-medium text-white ${PLAN_COLORS[planName]}`}>
                {planName.replace('_', ' ').toUpperCase()}
              </div>
              {planData.price_per_minute_usd > 0 && (
                <span className="text-green-400 text-xs font-medium">
                  ${planData.price_per_minute_usd}/min
                </span>
              )}
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-1"><Bot className="w-3 h-3" /> Agents</span>
                <span className="text-white">{planData.max_voice_agents}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-1"><Megaphone className="w-3 h-3" /> Campaigns</span>
                <span className="text-white">{planData.max_campaigns}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-1"><Users className="w-3 h-3" /> Users</span>
                <span className="text-white">{planData.max_end_users}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Minutes</span>
                <span className="text-white">{planData.max_minutes_per_month.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Concurrent</span>
                <span className="text-white">{planData.max_concurrent_calls}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Client List */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Client Resource Allocations</h3>
        </div>
        <div className="divide-y divide-gray-700">
          {clients.map((client) => (
            <div key={client.client_id} className="p-4" data-testid={`client-row-${client.client_id}`}>
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(client.client_id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{client.client_name}</p>
                    <p className="text-sm text-gray-400">{client.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`px-2 py-1 rounded text-xs font-medium text-white ${PLAN_COLORS[client.plan_name] || 'bg-gray-500'}`}>
                    {(client.plan_name || 'free_trial').replace('_', ' ').toUpperCase()}
                  </div>
                  <div className="text-sm text-gray-400 hidden md:block">
                    <span className="text-white">{client.usage?.voice_agents}</span> agents |
                    <span className="text-white"> {client.usage?.campaigns}</span> campaigns |
                    <span className="text-white"> {client.usage?.end_users}</span> users
                  </div>
                  {expandedClient === client.client_id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedClient === client.client_id && clientQuota && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Current Usage */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                        <Package className="w-4 h-4" /> Current Usage
                      </h4>
                      <div className="space-y-3">
                        <UsageBar 
                          label="Voice Agents" 
                          current={clientQuota.usage?.voice_agents || 0}
                          max={clientQuota.quota?.max_voice_agents || 5}
                          icon={Bot}
                        />
                        <UsageBar 
                          label="Campaigns" 
                          current={clientQuota.usage?.campaigns || 0}
                          max={clientQuota.quota?.max_campaigns || 10}
                          icon={Megaphone}
                        />
                        <UsageBar 
                          label="End Users" 
                          current={clientQuota.usage?.end_users || 0}
                          max={clientQuota.quota?.max_end_users || 20}
                          icon={Users}
                        />
                        <UsageBar 
                          label="Monthly Minutes" 
                          current={clientQuota.usage?.minutes_this_month || 0}
                          max={clientQuota.quota?.max_minutes_per_month || 5000}
                          icon={Clock}
                        />
                        <UsageBar 
                          label="Concurrent Calls" 
                          current={clientQuota.usage?.concurrent_calls || 0}
                          max={clientQuota.quota?.max_concurrent_calls || 2}
                          icon={Phone}
                        />
                      </div>
                      {/* Pricing Info */}
                      {clientQuota.pricing && (
                        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-green-400 text-sm">
                            <DollarSign className="w-4 h-4" />
                            <span>Est. cost: ${clientQuota.pricing.estimated_monthly_cost_usd}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            @ ${clientQuota.pricing.price_per_minute_usd}/min
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Quota Settings / Edit */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-300">Quota Settings</h4>
                        {editMode === client.client_id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); saveCustomQuota(client.client_id); }}
                              disabled={updating === client.client_id}
                              className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs flex items-center gap-1 hover:bg-green-500/30"
                            >
                              <Save className="w-3 h-3" /> Save
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                              className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs flex items-center gap-1 hover:bg-gray-600"
                            >
                              <X className="w-3 h-3" /> Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); startEdit(client.client_id); }}
                            className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs flex items-center gap-1 hover:bg-blue-500/30"
                          >
                            <Edit3 className="w-3 h-3" /> Edit
                          </button>
                        )}
                      </div>
                      
                      {editMode === client.client_id ? (
                        <div className="space-y-3">
                          <QuotaInput 
                            label="Max Voice Agents" 
                            value={editValues.max_voice_agents}
                            onChange={(v) => setEditValues({...editValues, max_voice_agents: parseInt(v) || 0})}
                          />
                          <QuotaInput 
                            label="Max Campaigns" 
                            value={editValues.max_campaigns}
                            onChange={(v) => setEditValues({...editValues, max_campaigns: parseInt(v) || 0})}
                          />
                          <QuotaInput 
                            label="Max End Users" 
                            value={editValues.max_end_users}
                            onChange={(v) => setEditValues({...editValues, max_end_users: parseInt(v) || 0})}
                          />
                          <QuotaInput 
                            label="Monthly Minutes" 
                            value={editValues.max_minutes_per_month}
                            onChange={(v) => setEditValues({...editValues, max_minutes_per_month: parseInt(v) || 0})}
                          />
                          <QuotaInput 
                            label="Max Concurrent Calls" 
                            value={editValues.max_concurrent_calls}
                            onChange={(v) => setEditValues({...editValues, max_concurrent_calls: parseInt(v) || 0})}
                          />
                          <QuotaInput 
                            label="Price/Min (USD)" 
                            value={editValues.price_per_minute_usd}
                            onChange={(v) => setEditValues({...editValues, price_per_minute_usd: parseFloat(v) || 0})}
                            step="0.01"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Voice Agents</span>
                            <span className="text-white">{clientQuota.quota?.max_voice_agents}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Campaigns</span>
                            <span className="text-white">{clientQuota.quota?.max_campaigns}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">End Users</span>
                            <span className="text-white">{clientQuota.quota?.max_end_users}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Monthly Minutes</span>
                            <span className="text-white">{clientQuota.quota?.max_minutes_per_month?.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Concurrent Calls</span>
                            <span className="text-white">{clientQuota.quota?.max_concurrent_calls}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Price/Min</span>
                            <span className="text-green-400">${clientQuota.quota?.price_per_minute_usd}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Quick Plan Selection */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-300">Apply Package</h4>
                      <div className="space-y-2">
                        {Object.keys(plans).map((planName) => (
                          <button
                            key={planName}
                            onClick={(e) => {
                              e.stopPropagation();
                              applyPlan(client.client_id, planName);
                            }}
                            disabled={updating === client.client_id}
                            className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${
                              clientQuota.plan_name === planName
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            } ${updating === client.client_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${PLAN_COLORS[planName]}`} />
                              {planName.replace('_', ' ')}
                            </span>
                            {clientQuota.plan_name === planName && <Check className="w-4 h-4" />}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Applying a package will override custom quota settings
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {clients.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No clients found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Usage Bar Component
const UsageBar = ({ label, current, max, icon: Icon }) => {
  const percentage = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const isHigh = percentage >= 80;
  const isCritical = percentage >= 95;
  
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-400 flex items-center gap-1">
          <Icon className="w-3 h-3" /> {label}
        </span>
        <span className={`${isCritical ? 'text-red-400' : isHigh ? 'text-yellow-400' : 'text-white'}`}>
          {typeof current === 'number' ? current.toLocaleString() : current} / {max.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${
            isCritical ? 'bg-red-500' : isHigh ? 'bg-yellow-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Quota Input Component
const QuotaInput = ({ label, value, onChange, step = "1" }) => (
  <div className="flex items-center justify-between gap-2">
    <label className="text-xs text-gray-400 flex-1">{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      step={step}
      min="0"
      onClick={(e) => e.stopPropagation()}
      className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm text-right focus:outline-none focus:border-blue-500"
    />
  </div>
);

export default ClientResourceManager;
